import * as React from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import {cn} from '../lib/cn';
import {getCustomContainer} from '../lib/utils';
import {useTooltipTouch, type TooltipTouchHandlers} from './useTooltipTouch';

export function TooltipProvider({
  delayDuration = 0,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delayDuration={delayDuration}
      {...props}
    />
  );
}

export const TooltipRoot = TooltipPrimitive.Root;

export interface TooltipRootProps extends React.ComponentProps<
  typeof TooltipPrimitive.Root
> {
  disabled?: boolean;
}

export function TooltipRootWrapper({disabled = false, ...props}: TooltipRootProps) {
  return (
    <TooltipProvider>
      <TooltipPrimitive.Root
        data-slot="tooltip"
        open={disabled ? false : undefined}
        {...props}
      />
    </TooltipProvider>
  );
}

export function TooltipTrigger(
  props: React.ComponentProps<typeof TooltipPrimitive.Trigger>,
) {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />;
}

export interface TooltipContentProps extends React.ComponentProps<
  typeof TooltipPrimitive.Content
> {
  variant?: 'light' | 'dark';
  container?: HTMLElement | null;
}

export function TooltipContent({
  className,
  sideOffset = 0,
  children,
  variant = 'light',
  container,
  ...props
}: TooltipContentProps) {
  return (
    <TooltipPrimitive.Portal container={container ?? getCustomContainer()}>
      <TooltipPrimitive.Content
        data-slot="tooltip-content"
        sideOffset={sideOffset}
        className={cn(
          'animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out',
          'data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
          'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2',
          'data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
          'origin-[var(--radix-tooltip-content-transform-origin)] z-50 w-fit text-balance',
          'rounded-md px-4 py-2 text-sm font-medium',
          {
            'bg-[var(--chekin-color-white)] text-[var(--chekin-color-brand-navy)] shadow-[0_0_10px_0_rgba(143,143,143,0.30)]':
              variant === 'light',
            'bg-[var(--chekin-color-brand-navy)] text-[var(--chekin-color-white)]':
              variant === 'dark',
          },
          className,
        )}
        {...props}
      >
        {children}
        <TooltipPrimitive.Arrow
          className={cn(
            'z-50 size-3 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-[2px]',
            {
              'bg-[var(--chekin-color-white)] fill-[var(--chekin-color-white)]':
                variant === 'light',
              'bg-[var(--chekin-color-brand-navy)] fill-[var(--chekin-color-brand-navy)]':
                variant === 'dark',
            },
          )}
        />
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  );
}

export interface TooltipProps
  extends
    Omit<TooltipRootProps, 'children'>,
    Pick<TooltipContentProps, 'side' | 'sideOffset' | 'variant' | 'container'> {
  content?: React.ReactNode;
  children: React.ReactNode;
  asChild?: boolean;
  contentClassName?: string;
  delayDuration?: number;
  longPressDuration?: number;
}

export function Tooltip({
  content,
  children,
  side = 'top',
  sideOffset = 0,
  variant = 'light',
  container,
  contentClassName,
  asChild = true,
  delayDuration = 150,
  disabled = false,
  longPressDuration = 500,
  ...props
}: TooltipProps) {
  if (typeof content === 'undefined') {
    return (
      <TooltipProvider delayDuration={delayDuration}>
        <TooltipPrimitive.Root
          data-slot="tooltip"
          open={disabled ? false : undefined}
          {...props}
        >
          {children}
        </TooltipPrimitive.Root>
      </TooltipProvider>
    );
  }

  return (
    <TooltipProvider delayDuration={delayDuration}>
      <TooltipTouchRoot
        disabled={disabled}
        longPressDuration={longPressDuration}
        {...props}
      >
        {triggerHandlers => (
          <>
            <TooltipPrimitive.Trigger
              data-slot="tooltip-trigger"
              asChild={asChild}
              {...triggerHandlers}
            >
              {children}
            </TooltipPrimitive.Trigger>
            <TooltipContent
              side={side}
              sideOffset={sideOffset}
              variant={variant}
              container={container}
              className={contentClassName}
            >
              {content}
            </TooltipContent>
          </>
        )}
      </TooltipTouchRoot>
    </TooltipProvider>
  );
}

interface TooltipTouchRootProps extends Omit<TooltipRootProps, 'children'> {
  longPressDuration: number;
  children: (triggerHandlers: TooltipTouchHandlers) => React.ReactNode;
}

function TooltipTouchRoot({
  disabled,
  longPressDuration,
  children,
  open: openProp,
  onOpenChange,
  defaultOpen,
  ...props
}: TooltipTouchRootProps) {
  const {open, setOpen, handlers} = useTooltipTouch({
    disabled,
    longPressDuration,
    open: openProp,
    onOpenChange,
    defaultOpen,
  });

  return (
    <TooltipPrimitive.Root
      data-slot="tooltip"
      open={open}
      onOpenChange={setOpen}
      {...props}
    >
      {children(handlers)}
    </TooltipPrimitive.Root>
  );
}
